interface ArticleSectionProps {
  scrollTo: (id: string) => void;
}

export default function ArticleSection({ scrollTo }: ArticleSectionProps) {
  const tasksCards = [
    {
      icon: 'ri-line-chart-line',
      title: 'Рост продаж',
      desc: '<strong>Обучение сотрудников</strong> техникам продаж увеличивает конверсию и средний чек. Команды, прошедшие корпоративные тренинги, показывают рост выручки на 20-40%.'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Улучшение сервиса',
      desc: '<strong>Развитие персонала</strong> в области клиентского сервиса снижает количество жалоб и повышает лояльность. Довольные клиенты возвращаются и рекомендуют компанию.'
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Повышение вовлечённости',
      desc: 'Когда компания инвестирует в <strong>развитие сотрудников</strong>, люди чувствуют свою ценность. Вовлечённые команды работают продуктивнее и реже уходят.'
    },
    {
      icon: 'ri-user-star-line',
      title: 'Развитие руководителей',
      desc: '<strong>Обучение руководителей</strong> управленческим навыкам напрямую влияет на эффективность всей команды. Сильный лидер создаёт сильную команду.'
    },
    {
      icon: 'ri-hand-heart-line',
      title: 'Удержание сотрудников',
      desc: 'Возможность <strong>обучения и развития</strong> — одна из главных причин, по которым талантливые люди остаются в компании. Обучение снижает текучесть кадров.'
    },
    {
      icon: 'ri-error-warning-line',
      title: 'Снижение ошибок',
      desc: 'Системное <strong>корпоративное обучение</strong> помогает стандартизировать процессы и снизить количество дорогостоящих ошибок сотрудников.'
    },
    {
      icon: 'ri-building-2-line',
      title: 'Развитие корпоративной культуры',
      desc: 'Совместное <strong>обучение команды</strong> формирует общие ценности и укрепляет корпоративную культуру. Единое информационное поле — основа сильной компании.'
    },
    {
      icon: 'ri-user-add-line',
      title: 'Адаптация новичков',
      desc: '<strong>Система обучения персонала</strong> ускоряет вхождение новых сотрудников в должность. Структурированная адаптация сокращает время выхода на результат в 2-3 раза.'
    }
  ];

  return (
    <>
      {/* Что такое корпоративное обучение сотрудников */}
      <section id="what-is" className="py-20 md:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-8 text-center">
              Что такое корпоративное обучение сотрудников
            </h2>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              <strong>Корпоративное обучение</strong> — это система развития профессиональных знаний, навыков и компетенций сотрудников компании, направленная на достижение бизнес-целей организации. Это комплексный подход к <strong>развитию персонала</strong>, который включает в себя не только передачу знаний, но и изменение поведения, формирование новых привычек и внедрение эффективных моделей работы.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4">Для чего нужно корпоративное обучение?</h3>
            <p className="text-gray-600 mb-8 leading-relaxed">
              В современном бизнесе <strong>обучение сотрудников</strong> — это не дополнительная опция, а стратегическая необходимость. Компании, которые системно занимаются <strong>развитием сотрудников</strong>, быстрее адаптируются к изменениям рынка, внедряют инновации и опережают конкурентов. <strong>Корпоративное развитие</strong> создаёт фундамент для устойчивого роста бизнеса в долгосрочной перспективе.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4">Какие задачи решает обучение персонала?</h3>
            <p className="text-gray-600 mb-8 leading-relaxed">
              <strong>Обучение персонала</strong> решает широкий спектр бизнес-задач: от повышения продаж и улучшения клиентского сервиса до развития управленческих компетенций и укрепления корпоративной культуры. Системное <strong>обучение сотрудников компании</strong> помогает выстроить единые стандарты работы, снизить количество ошибок и повысить общую эффективность бизнес-процессов.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4">Почему обучение нельзя заменить наймом новых сотрудников?</h3>
            <p className="text-gray-600 mb-8 leading-relaxed">
              Многие компании ошибочно полагают, что проще нанять готового специалиста, чем обучать существующего. Однако найм — это всегда лотерея: новый сотрудник может не вписаться в культуру компании, а затраты на поиск и адаптацию часто превышают стоимость <strong>корпоративного обучения</strong>. К тому же, постоянная замена людей демотивирует команду и разрушает накопленную экспертизу.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4">Чем отличается обучение от мотивации?</h3>
            <p className="text-gray-600 mb-8 leading-relaxed">
              Мотивация отвечает на вопрос «почему я должен это делать», а <strong>развитие сотрудников</strong> — на вопрос «как мне это делать эффективно». Без обучения мотивация быстро угасает, потому что люди теряют уверенность в своих силах. Без мотивации обучение не даёт результата, потому что люди не видят смысла в изменениях. Эффективная стратегия <strong>корпоративного развития</strong> всегда сочетает оба компонента.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4">Почему обучение должно быть системой?</h3>
            <p className="text-gray-600 leading-relaxed">
              Разовые тренинги дают краткосрочный эффект, который быстро сходит на нет. Только системное <strong>обучение сотрудников компании</strong> — с диагностикой, аудитом, программой, практикой, сопровождением и оценкой результатов — приводит к устойчивым изменениям. <strong>Корпоративное обучение</strong> должно быть встроено в бизнес-процессы, а не существовать как отдельное мероприятие. Именно такой подход мы реализуем в программах Business Training.
            </p>

            <div className="mt-8 p-5 bg-teal-50 border border-teal-200 rounded-xl">
              <p className="text-sm text-gray-700 leading-relaxed">
                <i className="ri-lightbulb-line text-teal-600 mr-1"></i>
                <strong>Совет:</strong> Если необходимо сначала определить реальные причины низких продаж — рекомендуем начать с{' '}
                <a href="/enciklopediya-korporativnogo-obucheniya/obuchenie-otdela-prodazh" className="text-teal-600 font-semibold underline hover:text-teal-700">экспресс-диагностики отдела продаж</a>. Если же требуется длительная работа над системой продаж — подойдёт{' '}
                <a href="/enciklopediya-korporativnogo-obucheniya/razrabotka-programm-obucheniya" className="text-teal-600 font-semibold underline hover:text-teal-700">разработка корпоративной программы обучения</a>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Какие задачи решает корпоративное обучение */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 text-center mb-6">
            Какие задачи решает корпоративное обучение
          </h2>
          <p className="text-lg text-gray-600 text-center mb-16 max-w-3xl mx-auto">
            Системное развитие персонала помогает бизнесу решать конкретные задачи — от роста выручки до укрепления корпоративной культуры
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {tasksCards.map((card, i) => (
              <div key={i} className="bg-gray-50 p-7 rounded-xl hover:bg-white hover:ring-2 hover:ring-teal-200 transition-all group">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <i className={`${card.icon} text-xl text-white`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{card.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: card.desc }}></p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <button onClick={() => scrollTo('contact')} className="bg-teal-600 text-white px-8 py-4 rounded-lg text-base font-semibold hover:bg-teal-700 transition-all whitespace-nowrap cursor-pointer">
              Обсудить задачи вашей компании
            </button>
          </div>

          <div className="max-w-4xl mx-auto mt-10 p-5 bg-teal-50 border border-teal-200 rounded-xl">
            <p className="text-sm text-gray-700 leading-relaxed">
              <i className="ri-lightbulb-line text-teal-600 mr-1"></i>
              <strong>Рекомендация:</strong> Для развития навыков менеджеров и руководителей рекомендуем посмотреть{' '}
              <a href="/enciklopediya-korporativnogo-obucheniya/trening-dlya-rukovoditeley" className="text-teal-600 font-semibold underline hover:text-teal-700">программы обучения руководителей</a>, а для комплексного подхода —{' '}
              <a href="/enciklopediya-korporativnogo-obucheniya/chto-takoe-korporativnoe-obuchenie" className="text-teal-600 font-semibold underline hover:text-teal-700">корпоративные тренинги</a> и{' '}
              <a href="/enciklopediya-korporativnogo-obucheniya/komandoobrazovanie" className="text-teal-600 font-semibold underline hover:text-teal-700">бизнес-игры</a>.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}